import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Download, Calendar, User, Mail, CheckCircle, Plus, Search } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import BackButton from '../components/BackButton';
import { getToken } from '../services/tokenService';

const DOCUMENT_TYPES_LABELS = {
  bulletin_adhesion: "Bulletin d'Adhésion",
  conditions_generales: 'Conditions Générales',
  convention_mise_disposition: 'Convention de Mise à Disposition',
  fiche_bilan_mission: 'Fiche de Bilan de Mission',
};

export default function ContractsList() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchContracts();
  }, []);

  const fetchContracts = async () => {
    try {
      const token = getToken();
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/contracts/list`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Erreur lors du chargement des contrats');
      }

      const data = await response.json();
      setContracts(data.contracts || []);
    } catch {
      toast.error('Impossible de charger les contrats');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (contract) => {
    try {
      const token = getToken();
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/contracts/generate/${contract.document_type}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(contract.document_data),
        }
      );

      if (!response.ok) {
        throw new Error('Erreur lors du téléchargement');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${contract.reference}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success('Document téléchargé avec succès');
    } catch {
      toast.error('Erreur lors du téléchargement du document');
    }
  };

  const handleVerify = (documentId) => {
    navigate(`/verify-document/${documentId}`);
  };

  const filteredContracts = contracts.filter((contract) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      contract.reference.toLowerCase().includes(searchLower) ||
      contract.signer_name.toLowerCase().includes(searchLower) ||
      contract.signer_email.toLowerCase().includes(searchLower) ||
      DOCUMENT_TYPES_LABELS[contract.document_type]?.toLowerCase().includes(searchLower)
    );
  });

  const canGenerateContracts =
    user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'cooper';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Bouton retour */}
        <BackButton label="Retour" className="mb-4" />
        
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Mes Contrats</h1>
            <p className="text-gray-600">
              {contracts.length} document{contracts.length > 1 ? 's' : ''} signé
              {contracts.length > 1 ? 's' : ''}
            </p>
          </div>
          {canGenerateContracts && (
            <Button
              onClick={() => navigate('/member/contracts/new')}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nouveau contrat
            </Button>
          )}
        </div>

        {/* Barre de recherche */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Rechercher par référence, nom, email ou type..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </CardContent>
        </Card>

        {filteredContracts.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {searchTerm ? 'Aucun résultat' : 'Aucun contrat'}
              </h3>
              <p className="text-gray-600 mb-6">
                {searchTerm
                  ? 'Essayez avec un autre terme de recherche'
                  : 'Vous n\'avez pas encore de contrat signé'}
              </p>
              {canGenerateContracts && !searchTerm && (
                <Button
                  onClick={() => navigate('/member/contracts/new')}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Créer mon premier contrat
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 gap-4 md:gap-6">
            {filteredContracts.map((contract) => (
              <Card key={contract.document_id} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                        <FileText className="w-6 h-6 text-blue-600" />
                      </div>
                      <div>
                        <div className="text-lg font-semibold">
                          {DOCUMENT_TYPES_LABELS[contract.document_type] || contract.document_type}
                        </div>
                        <div className="text-sm font-normal text-gray-600">
                          {contract.reference}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-green-600">
                      <CheckCircle className="w-5 h-5" />
                      <span className="text-sm font-medium">Signé</span>
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    <div className="flex items-center gap-2 text-gray-600">
                      <User className="w-4 h-4" />
                      <div>
                        <div className="text-xs text-gray-500">Signataire</div>
                        <div className="text-sm font-medium">{contract.signer_name}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-gray-600">
                      <Mail className="w-4 h-4" />
                      <div>
                        <div className="text-xs text-gray-500">Email</div>
                        <div className="text-sm font-medium">{contract.signer_email}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-gray-600">
                      <Calendar className="w-4 h-4" />
                      <div>
                        <div className="text-xs text-gray-500">Date de signature</div>
                        <div className="text-sm font-medium">
                          {new Date(contract.signed_at).toLocaleDateString('fr-FR', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleDownload(contract)}
                      variant="outline"
                      className="flex-1"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Télécharger PDF
                    </Button>
                    <Button
                      onClick={() => handleVerify(contract.document_id)}
                      variant="outline"
                      className="flex-1"
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Vérifier l'authenticité
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
